import type { ReactNode } from 'react';

/**
 * The standard page header: heading + description on the left, an action cluster flush right.
 *
 * The text block is hidden on mobile — the top bar shows the heading there (see {@link usePageTitle}),
 * so repeating it just wastes width and pushes the actions into overflow; `max-md:justify-end` keeps
 * the cluster flush right once the text is gone and the row would otherwise be empty. `self-start`
 * holds the text at the row top instead of centring it against a taller control (the month picker),
 * while `items-center` keeps the controls themselves centred against it. A cluster too wide for the
 * remaining room wraps to its own line rather than squeezing the heading.
 *
 * Order the [actions] info → filter → utility → primary, with the primary action last so it owns the
 * right edge (the same anchor the CTA takes on the pages that have no other controls).
 */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 max-md:justify-end">
      <div className="self-start max-md:hidden">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description == null ? null : (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {actions == null ? null : (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
    </div>
  );
}
